<template>
  <div class="main-container">
    <h1>Chat Message Management</h1>

    <!-- Header with Filters -->
    <div id="buttons-div">
      <div class="form-group">
        <label class="form-label" for="chatbot">Chatbot</label>
        <select id="chatbot" v-model="selectedChatbotId" @change="fetchChatMessages" class="input-field">
          <option v-for="chatbot in chatbots" :key="chatbot.id" :value="chatbot.id">
            {{ chatbot.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label" for="fromDate">From</label>
        <input id="fromDate" v-model="fromDate" type="date" placeholder="From Date" class="input-field" />
      </div>
      <div class="form-group">
        <label class="form-label" for="toDate">To</label>
        <input id="toDate" v-model="toDate" type="date" placeholder="To Date" class="input-field" />
      </div>
      <button class="pill-button accent" id="refreshButton" @click="fetchChatMessages">Fetch Messages</button>
    </div>

    <!-- Content -->
    <div class="content">
      <!-- Left Pane -->
      <div class="left-pane">
        <h2>Conversations</h2>
        <ul class="conversation-list">
          <li v-for="(group, chatId) in groupedConversations" :key="chatId" @click="selectChat(chatId)"
            :class="{ selected: chatId === selectedChatId }">
            <div>
              <small class="conversation-id">ID: {{ chatId }}</small><br>
              <strong>User:</strong> {{ getFirstUserMessage(group).slice(0, 20) }}...<br>
              <strong>Bot:</strong> {{ getFirstBotMessage(group).slice(0, 20) }}...<br>
              <small>{{ formatDate(group[0].createdDate) }}</small>
            </div>
          </li>
        </ul>
      </div>

      <!-- Right Pane -->
      <div class="right-pane">
        <h2>Messages for Chat ID: {{ selectedChatId }}</h2>
        <Spinner :show="isLoading" />

        <div v-if="selectedChatId && filteredMessages.length > 0">
          <div v-for="message in filteredMessages" :key="message.id" class="message-item">
            <div v-if="message.role === 'apiMessage'" class="bot-message">
              <strong>Bot:</strong>
              <vue-markdown-it :source="message.content" />
              <small>{{ formatDate(message.createdDate) }}</small>
            </div>
            <div v-else class="user-message">
              <strong>User:</strong>
              <vue-markdown-it :source="message.content" />
              <small>{{ formatDate(message.createdDate) }}</small>
            </div>
          </div>
        </div>

        <p v-if="selectedChatId && filteredMessages.length === 0 && !isLoading">
          No messages available for this period.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { VueMarkdownIt } from '@f3ve/vue-markdown-it';
import Spinner from '@/components/SpinnerComponent.vue';

export default {
  components: {
    VueMarkdownIt,
    Spinner,
  },
  data() {
    return {
      apiUrl: 'https://plainbots.plainbots.co.za/api/v1',
      bearerToken: 'xwiGJzdlDWd2GsD3C9uyx9NYgHoJ6KoFatLiMyOYlsU',
      messages: [], // All messages fetched
      selectedChatId: null, // To store selected chat ID
      isLoading: false, // To track loading state
      fromDate: '', // From Date filter
      toDate: '', // To Date filter
      chatbots: JSON.parse(localStorage.getItem('chatbotRecords')) || [],
    };
  },
  computed: {
    // Group messages by chat ID and filter by date
    groupedConversations() {
      const from = new Date(this.fromDate);
      from.setHours(0, 0, 0, 1); // Start of the day

      const to = new Date(this.toDate);
      to.setHours(23, 59, 59, 999); // End of the day

      const grouped = this.messages.reduce((acc, message) => {
        const msgDate = new Date(message.createdDate);
        if (msgDate >= from && msgDate <= to) {
          if (!acc[message.chatId]) {
            acc[message.chatId] = [];
          }
          acc[message.chatId].push(message);
        }
        return acc;
      }, {});

      // Sort conversations by latest message date
      const sortedGroups = Object.entries(grouped).sort((a, b) => {
        const lastMsgA = new Date(a[1][a[1].length - 1].createdDate);
        const lastMsgB = new Date(b[1][b[1].length - 1].createdDate);
        return lastMsgB - lastMsgA; // Sort descending
      });

      // Convert back to object
      return Object.fromEntries(sortedGroups);
    },

    // Filter messages for the selected chat ID
    filteredMessages() {
      if (!this.selectedChatId) return [];
      const filtered = this.groupedConversations[this.selectedChatId] || [];

      // Sort messages in descending order by createdDate
      return filtered.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    },

  },
  methods: {
    async fetchChatMessages() {
      this.isLoading = true;

      const url = `${this.apiUrl}/chatmessage/${this.selectedChatbotId}`;
      const headers = {
        Authorization: `Bearer ${this.bearerToken}`,
        'Content-Type': 'application/json',
      };

      try {
        const response = await fetch(url, { method: 'GET', headers });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // console.log('Response Data:', data);  // Debugging

        // Store messages
        this.messages = data || [];

      } catch (error) {
        console.error('Error fetching chat messages:', error);
      } finally {
        this.isLoading = false; // Stop the loading spinner
      }
    },
    selectChat(chatId) {
      this.selectedChatId = chatId; // Set selected chat ID
    },
    getFirstUserMessage(group) {
      const userMessage = group.find(msg => msg.role !== 'apiMessage');
      return userMessage ? userMessage.content : 'No user message';
    },
    getFirstBotMessage(group) {
      const botMessage = group.find(msg => msg.role === 'apiMessage');
      return botMessage ? botMessage.content : 'No bot response';
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    getLastDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  mounted() {
    // Set initial dates to today's date (last day)
    this.fromDate = this.getLastDate();
    this.toDate = this.getLastDate();

    if (this.chatbots.length > 0) {
      this.selectedChatbotId = this.chatbots[0].id;
      this.fetchChatMessages();
    }
  },
};
</script>

<style scoped>
.main-container {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 20px;
  color: var(--text-primary);
}

h1 {
  font-size: 2rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
  text-align: center;
}

#buttons-div {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.content {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  height: 70vh;
  margin-top: 20px;
}

.left-pane {
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-2xl);
  background: var(--bg-glass);
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(10px);
  overflow-y: auto;
}

.right-pane {
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-2xl);
  background: var(--bg-glass);
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(10px);
  overflow-y: auto;
}

.conversation-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;
}

.conversation-list li {
  background: var(--bg-glass);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border-color);
  padding: 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.conversation-list li:hover {
  box-shadow: var(--shadow-card);
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.18);
}

.conversation-list li.selected {
  border-color: rgba(249, 115, 22, 0.6);
  box-shadow: var(--shadow-card);
}

.message-item {
  background: var(--bg-glass);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-card);
  margin-bottom: 16px;
  padding: 15px;
  color: var(--text-primary);
}

@media (max-width: 900px) {
  .content {
    grid-template-columns: 1fr;
    height: auto;
  }
}
</style>
